import React from "react";
import { Plus, BookOpen } from "lucide-react";
import { useNavigate, useOutletContext } from "react-router-dom";
import Frame_y from "../../Images/Frame_yellow.svg";
import Calander from "../../Images/calender.svg";
import calendar from "../../Images/calendar.svg";
import Frame_Yellow from "../../Images/Frame_yellow.svg";
import Frame_b from "../../Images/Frame_b.svg";
import Frame from "../../Images/Frame.svg";
import LineChart from "../../Images/line.svg";

const MyCourses = () => {
  const navigate = useNavigate();
  const { userData, courses: schedules, courseCounts } = useOutletContext(); // ✅ Get courseCounts from context

  const handleAddNew = () => {
    navigate("/myprograms/add-new");
  };

  const handleCardClick = (scheduleId) => {
    navigate(`/myprograms/stu_mlt?schedule_id=${scheduleId}`);
  };

  // Calculate final price with discount
  const calculateFinalPrice = (amount, discountType, discountValue) => {
    if (!discountType || !discountValue) return amount;

    if (discountType === "Percentage") {
      return amount - (amount * discountValue) / 100;
    }
    return amount - discountValue;
  };

  // Get course status based on API status field
  const getCourseStatus = (schedule) => {
    const currentDate = new Date();
    currentDate.setHours(0, 0, 0, 0); // Reset to start of day for accurate comparison

    const startDate = new Date(schedule.start_date);
    startDate.setHours(0, 0, 0, 0);

    const endDate = new Date(schedule.end_date);
    endDate.setHours(0, 0, 0, 0);

    // Priority 1: Use API status if available
    if (schedule.status === "COMPLETED") return "completed";

    // Priority 2: Check dates
    if (startDate > currentDate) return "upcoming"; // Course hasn't started yet
    if (endDate < currentDate) return "completed"; // Course ended yesterday or before

    // If today is between start and end (inclusive), it's ongoing
    return "ongoing";
  };

  /* ---------------- EMPTY STATE ---------------- */
  if (!schedules || schedules.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full py-20 text-gray-600">
        <BookOpen size={60} className="text-gray-400 mb-4" />
        <h2 className="text-lg font-semibold">No enrolled programs yet</h2>
        <p className="text-sm text-gray-500 mt-1">
          Enroll in a course to get started.
        </p>
        <button
          className="mt-5 flex items-center gap-2 bg-teal-500 hover:bg-teal-600 text-white px-4 py-2 rounded-lg font-semibold transition-colors"
          onClick={handleAddNew}
        >
          <Plus size={18} /> Browse Programs
        </button>
      </div>
    );
  }

  /* ---------------- ✅ USE API COUNTS DIRECTLY ---------------- */
  const activeCount = courseCounts?.active_courses || 0;
  const completedCount = courseCounts?.completed_courses || 0;
  const upcomingCount = courseCounts?.upcoming_courses || 0;

  /* ---------------- RECOMMENDED ---------------- */
  const recommended = [
    {
      title: "Critical Care Nursing",
      rating: 5,
      image:
        "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=100&h=100&fit=crop",
    },
    {
      title: "Pediatric Nursing",
      rating: 5,
      image:
        "https://images.unsplash.com/photo-1532187863486-abf9dbad1b69?w=100&h=100&fit=crop",
    },
    {
      title: "ECG & Cardiac Care",
      rating: 4,
      image:
        "https://images.unsplash.com/photo-1551076805-e1869033e561?w=100&h=100&fit=crop",
    },
  ];

  return (
    <div className="flex flex-col lg:flex-row gap-6 p-6 min-h-screen">
      {/* ================= LEFT - COURSES GRID ================= */}
      <div className="flex-1">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-gray-900">My Programs</h1>
          <button
            onClick={handleAddNew}
            className="flex items-center gap-2 bg-[#4AACC5] hover:bg-[#3a9db5] text-white px-5 py-2.5 rounded-lg font-semibold transition-colors"
          >
            <Plus size={18} /> Add New
          </button>
        </div>

        {/* Courses Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {schedules.map((schedule) => {
            const course = schedule.course;
            const instructor =
              course?.instructors?.[0]?.user?.full_name || "Instructor";
            const instructorAvatar = course?.instructors?.[0]?.user?.avatar_url;
            const finalPrice = calculateFinalPrice(
              course?.amount || 0,
              schedule.discount_type,
              schedule.discount_value,
            );

            const status = getCourseStatus(schedule);
            const isCompleted = status === "completed";
            const isCourseUpcoming = status === "upcoming";

            const totalModules = course?.modules?.length || 0;
            const total_duration_days = schedule.total_duration_days || 0;

            return (
              <div
                key={schedule.schedule_id}
                onClick={() => handleCardClick(schedule.schedule_id)}
                className="w-full max-w-[288px] mx-auto cursor-pointer hover:scale-[1.02] transition-transform duration-200"
              >
                <div className="relative h-[384px]">
                  {/* IMAGE SECTION */}
                  <div className="absolute top-0 left-0 w-full h-52 rounded-t-lg overflow-hidden">
                    <img
                      src={
                        course?.program_thumbnail ||
                        "https://via.placeholder.com/288x208?text=Course+Image"
                      }
                      alt={course?.title || "Course"}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.src =
                          "https://via.placeholder.com/288x208?text=Course+Image";
                      }}
                    />
                  </div>

                  {/* CONTENT CARD */}
                  <div className="absolute top-[200px] left-0 w-full h-44 bg-white rounded-b-lg shadow-[0px_10px_60px_0px_rgba(0,0,0,0.10)] border border-gray-200">
                    <img
                      src={instructorAvatar || "https://via.placeholder.com/32"}
                      alt={instructor}
                      className="w-8 h-8 rounded-full object-cover absolute -top-4 left-7 border-2 border-white shadow-sm"
                      onError={(e) => {
                        e.target.src = "https://via.placeholder.com/32";
                      }}
                    />

                    <div
                      className={`absolute top-2 right-4 px-5 py-1 rounded-full text-xs font-normal ${
                        isCompleted
                          ? "bg-sky-100 text-teal-400"
                          : isCourseUpcoming
                            ? "bg-yellow-100 text-yellow-700"
                            : "bg-emerald-100 text-lime-800"
                      }`}
                    >
                      {isCompleted
                        ? "Completed"
                        : isCourseUpcoming
                          ? "Upcoming"
                          : "Ongoing"}
                    </div>

                    <div className="absolute top-8 left-7 text-[#4AACC5] text-xs font-normal truncate max-w-[180px]">
                      {instructor}
                    </div>

                    <h3 className="flex items-start absolute top-12 left-7 right-4 text-gray-900 text-base font-medium leading-tight line-clamp-2">
                      {course?.title || "Untitled Course"}
                    </h3>

                    <div className="absolute top-20 left-7 text-gray-900 text-base font-medium">
                      ${finalPrice.toFixed(2)}
                    </div>

                    <div className="absolute top-[117px] left-6 right-6 border-t border-zinc-200" />

                    <div className="absolute top-[133px] left-7 right-7 flex items-center gap-2 text-neutral-700 text-xs font-normal">
                      <span>
                        {totalModules} Module{totalModules !== 1 ? "s" : ""}
                      </span>
                      <div className="w-px h-2 bg-stone-300 rotate-[25deg]" />
                      <span>
                        {total_duration_days} Day
                        {total_duration_days !== 1 ? "s" : ""}
                      </span>
                      <div className="w-px h-2 bg-stone-300 rotate-[25deg]" />
                      <span className="whitespace-nowrap truncate">
                        {new Date(schedule.start_date).toLocaleDateString(
                          "en-US",
                          {
                            month: "short",
                            day: "2-digit",
                          },
                        )}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ================= RIGHT SIDEBAR ================= */}
      <div className="w-full lg:w-64 space-y-5">
        {/* Active Programs Card */}
        <div className="bg-white rounded-xl p-6 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <img src={Calander} alt="Active Programs" className="w-5 h-5" />
            <h4 className="font-semibold text-gray-900">Active Programs</h4>
          </div>
          <div className="flex items-center justify-between">
            <div className="text-3xl font-bold text-gray-900">
              {activeCount}
            </div>
            <img src={Frame_Yellow} alt="chart" className="h-14" />
          </div>
        </div>

        {/* Completed Programs Card */}
        <div className="bg-white rounded-xl p-6 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <img src={Frame} alt="Completed Programs" className="w-5 h-5" />
            <h4 className="font-semibold text-gray-900">Completed Programs</h4>
          </div>
          <div className="flex items-center justify-between">
            <div className="text-3xl font-bold text-gray-900">
              {completedCount}
            </div>
            <img src={LineChart} alt="chart" className="h-14" />
          </div>
        </div>
        {/* Upcoming Programs Card */}
        <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-all duration-200">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-4">
                <img
                  src={calendar}
                  alt="upcoming"
                  className="w-5 h-5 opacity-80"
                />
                <p className="text-gray-700 text-sm font-medium">
                  Upcoming Programs
                </p>
              </div>

              <div className="flex items-center justify-between gap-3 mb-2">
                <h2 className="text-4xl font-bold text-gray-900">
                  {upcomingCount}
                </h2>

                {/* Mini bar chart */}
                <img src={Frame_b} alt="chart" className="flex-shrink-0" />
              </div>
            </div>
          </div>
        </div>

        {/* Recommended Programs Card */}
        <div className="bg-white rounded-xl p-6 shadow-sm">
          <h4 className="font-semibold text-gray-900 mb-4">
            Recommended Programs
          </h4>
          <div className="space-y-4">
            {recommended.map((rec, index) => (
              <div
                key={index}
                className="flex items-start gap-4 pb-4 border-b last:border-0 cursor-pointer hover:bg-gray-50 -mx-2 px-2 py-2 rounded transition-colors"
              >
                <img
                  src={rec.image}
                  alt={rec.title}
                  className="w-16 h-16 rounded-md object-cover flex-shrink-0"
                  onError={(e) => {
                    e.target.src = "https://via.placeholder.com/64";
                  }}
                />
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-gray-500 mb-1">by Advanced</p>
                  <p className="font-semibold text-gray-900 text-sm mb-1 line-clamp-2">
                    {rec.title}
                  </p>
                  <div
                    className="flex text-yellow-400 text-sm"
                    aria-label={`Rating: ${rec.rating} out of 5 stars`}
                  >
                    {[...Array(5)].map((_, idx) => (
                      <span key={idx}>{idx < rec.rating ? "★" : "☆"}</span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MyCourses;
